import { $isListItemNode, $isListNode, type ListItemNode } from "@lexical/list";
import { $getRoot, $isElementNode, type LexicalNode } from "lexical";
import { DEFAULT_LIMITS } from "../../core/limits";

// Each level of indent wraps an item in another list and list item: two more levels of nesting.
const LEVELS_PER_INDENT = 2;

/** How many ancestors a node has: its path length in the stored document. */
function $depth(node: LexicalNode): number {
  let depth = 0;
  for (let parent = node.getParent(); parent !== null; parent = parent.getParent()) depth++;
  return depth;
}

/** How far below a node its deepest descendant sits. */
function $height(node: LexicalNode): number {
  if (!$isElementNode(node)) return 0;
  let height = 0;
  const stack: [LexicalNode, number][] = node.getChildren().map((child) => [child, 1]);
  while (stack.length > 0) {
    const [next, level] = stack.pop()!;
    height = Math.max(height, level);
    if ($isElementNode(next))
      for (const child of next.getChildren()) stack.push([child, level + 1]);
  }
  return height;
}

/** The item that only holds the nested list of the item before it. */
const $isWrapper = (node: LexicalNode | null): node is ListItemNode =>
  $isListItemNode(node) && node.getChildrenSize() === 1 && $isListNode(node.getFirstChild());

/**
 * Whether indenting these items keeps the document within the depth `validateDocument` allows.
 * An indented item takes the nested list that follows it along, so that list is measured too.
 */
export function $canIndent(
  items: readonly ListItemNode[],
  maxDepth: number = DEFAULT_LIMITS.maxDepth,
): boolean {
  return items.every((item) => {
    const next = item.getNextSibling();
    const moved = $isWrapper(next) ? [item, next] : [item];
    return moved.every((node) => $depth(node) + $height(node) + LEVELS_PER_INDENT <= maxDepth);
  });
}

/**
 * Outdents list items nested deeper than `validateDocument` allows, so that content built from
 * outside (Markdown) is always a document the editor will open again.
 */
export function $clampListDepth(maxDepth: number = DEFAULT_LIMITS.maxDepth): void {
  // Each pass outdents one item at least a level; the bound only guards against a pass that can't.
  for (let pass = 0; pass < DEFAULT_LIMITS.maxNodes; pass++) {
    const item = $findTooDeep($getRoot(), maxDepth);
    if (item === null) return;
    const excess = $depth(item) + $height(item) - maxDepth;
    const indent = item.getIndent();
    if (indent === 0) return;
    item.setIndent(Math.max(0, indent - Math.ceil(excess / LEVELS_PER_INDENT)));
  }
}

function $findTooDeep(root: LexicalNode, maxDepth: number): ListItemNode | null {
  const stack: [LexicalNode, number][] = [[root, 0]];
  while (stack.length > 0) {
    const [node, depth] = stack.pop()!;
    if ($isListItemNode(node) && !$isWrapper(node) && depth + $height(node) > maxDepth) return node;
    if ($isElementNode(node))
      for (const child of node.getChildren()) stack.push([child, depth + 1]);
  }
  return null;
}
